import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PasswordField, useNotify } from '@routes-plan/ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, FormActions, FormSection, FullRow } from '../shared/AdminUi';
import MobileDevicesPanel, { MobileDevice, MobileDeviceRequest } from './MobileDevicesPanel';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Field = 'firstName' | 'lastName' | 'mobilePhone' | 'username' | 'password' | 'password2';

/** Editar representante: datos, contacto, acceso y dispositivos de la app. */
const SalesRepEdit: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  setPageTitle('pageTitle.salesRepEdit');
  const { canUpdate } = useResourcePermissions('/admin/sales-reps');
  const navigate = useNavigate();
  const { id } = useParams();
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [address, setAddress] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [initialUsername, setInitialUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [mobileDevices, setMobileDevices] = useState<MobileDevice[]>([]);
  const [mobileDeviceRequests, setMobileDeviceRequests] = useState<MobileDeviceRequest[]>([]);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);
  const [deviceBusy, setDeviceBusy] = useState(false);

  const loadMobileDevices = async () => {
    try {
      const { data } = await axios.get(`sales-reps/${id}/mobile-devices`);
      setMobileDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching mobile devices:', error);
    }
  };

  const loadMobileDeviceRequests = async () => {
    try {
      const { data } = await axios.get(`sales-reps/${id}/mobile-device-requests`);
      setMobileDeviceRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching mobile device requests:', error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`sales-reps/${id}`);
        setIdNumber(data.idNumber ?? '');
        setFirstName(data.firstName ?? '');
        setLastName(data.lastName ?? '');
        setUsername(data.username ?? '');
        setInitialUsername(data.username ?? '');
        setPhone(data.phone ?? '');
        setMobilePhone(data.mobilePhone ?? '');
        setAddress(data.address ?? '');
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    })();
    void loadMobileDevices();
    void loadMobileDeviceRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const change = (setter: (v: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setIsModified(true);
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const req = t('common.required');
    const found: Partial<Record<Field, string>> = {};
    if (!firstName.trim()) found.firstName = req;
    if (!lastName.trim()) found.lastName = req;
    if (!mobilePhone.trim()) found.mobilePhone = req;
    if (!username.trim()) found.username = t('salesReps.usernameRequired');
    if (updatePassword && password !== password2) found.password2 = t('salesReps.passwordMismatch');
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('salesReps.missingFields'));
      return;
    }

    setSaving(true);
    try {
      const normalizedUsername = username.trim();
      if (normalizedUsername !== initialUsername.trim()) {
        const checkUsernameResponse = await axios.get(`users/check-username/${normalizedUsername}`);
        if (checkUsernameResponse.data.exists) {
          setErrors({ username: t('salesReps.usernameTaken') });
          notify.warning(t('salesReps.usernameTaken'));
          return;
        }
      }
      await axios.put(`sales-reps/${id}`, {
        idNumber,
        firstName,
        lastName,
        username,
        phone,
        email: '',
        password: updatePassword ? password : undefined,
        mobilePhone,
        address,
      });
      notify.success(t('salesReps.updatedOk'));
      navigate('/admin/sales-reps');
    } catch (error: any) {
      notify.error(error?.response?.data?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleMobileDeviceStatusChange = async (deviceId: number, status: 'approved' | 'blocked') => {
    setDeviceBusy(true);
    try {
      const { data } = await axios.put(`mobile-devices/${deviceId}/status`, { status });
      await loadMobileDevices();
      notify.success(data.message || t('salesReps.deviceStatusUpdated'));
    } catch (error: any) {
      notify.error(error?.response?.data?.message || t('salesReps.deviceStatusError'));
    } finally {
      setDeviceBusy(false);
    }
  };

  const handleMobileDeviceRequestStatusChange = async (requestId: number, status: 'approved' | 'rejected') => {
    setDeviceBusy(true);
    try {
      const { data } = await axios.put(`mobile-device-requests/${requestId}/status`, { status });
      await loadMobileDeviceRequests();
      await loadMobileDevices();
      notify.success(data.message || t('salesReps.requestUpdatedOk'));
    } catch (error: any) {
      notify.error(error?.response?.data?.message || t('salesReps.requestUpdateError'));
    } finally {
      setDeviceBusy(false);
    }
  };

  const err = (f: Field) => ({ error: !!errors[f], helperText: errors[f] });
  const title = t('pageTitle.salesRepEdit');
  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={[firstName, lastName].filter(Boolean).join(' ') || undefined}
        breadcrumbs={[{ label: t('salesReps.title'), href: '#/admin/sales-reps' }, { label: title }]}
      />
      <Box component="form" noValidate onSubmit={submit} autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormSection title={t('salesReps.sectionPerson')}>
          <TextField id="firstName" label={t('common.firstName')} value={firstName} onChange={change(setFirstName)} inputProps={{ maxLength: 30 }} required fullWidth {...err('firstName')} />
          <TextField id="lastName" label={t('common.lastName')} value={lastName} onChange={change(setLastName)} inputProps={{ maxLength: 30 }} required fullWidth {...err('lastName')} />
          <TextField
            id="idNumber"
            label={t('common.idNumber')}
            value={idNumber}
            InputProps={{ readOnly: true }}
            helperText={t('salesReps.idNumberLocked')}
            fullWidth
          />
        </FormSection>
        <FormSection title={t('salesReps.sectionContact')}>
          <TextField id="mobilePhone" type="tel" label={t('common.cellphone')} value={mobilePhone} onChange={change(setMobilePhone)} inputProps={{ maxLength: 10 }} required fullWidth {...err('mobilePhone')} />
          <TextField id="phone" type="tel" label={t('common.phone')} value={phone} onChange={change(setPhone)} inputProps={{ maxLength: 10 }} fullWidth />
        </FormSection>
        <FormSection title={t('salesReps.sectionAccess')}>
          <TextField id="username" label={t('common.username')} value={username} onChange={change(setUsername)} inputProps={{ maxLength: 30 }} required fullWidth autoComplete="off" {...err('username')} />
          <FullRow>
            <FormControlLabel
              control={
                <Switch
                  checked={updatePassword}
                  onChange={() => {
                    setUpdatePassword(!updatePassword);
                    setPassword('');
                    setPassword2('');
                    setIsModified(true);
                  }}
                />
              }
              label={t('salesReps.updatePassword')}
            />
          </FullRow>
          {updatePassword && (
            <>
              <PasswordField
                id="password"
                label={t('common.password')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setIsModified(true);
                }}
                inputProps={{ maxLength: 30, autoComplete: 'new-password' }}
                fullWidth
                {...err('password')}
              />
              <PasswordField
                id="password2"
                label={t('salesReps.confirmPassword')}
                value={password2}
                onChange={(e) => {
                  setPassword2(e.target.value);
                  setIsModified(true);
                }}
                inputProps={{ maxLength: 30, autoComplete: 'new-password' }}
                fullWidth
                {...err('password2')}
              />
            </>
          )}
        </FormSection>
        <FormActions
          cancelTo="/admin/sales-reps"
          saving={saving}
          allowed={canUpdate}
          deniedTooltip={t('common.noPermissionEdit')}
          disabled={!isModified}
        />
      </Box>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
          {t('salesReps.sectionDevices')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('salesReps.sectionDevicesHint')}
        </Typography>
        <MobileDevicesPanel
          devices={mobileDevices}
          requests={mobileDeviceRequests}
          onDeviceStatus={handleMobileDeviceStatusChange}
          onRequestStatus={handleMobileDeviceRequestStatusChange}
          disabled={deviceBusy}
        />
      </Paper>
    </AdminPage>
  );
};

export default SalesRepEdit;
